import { useRef, useState } from 'react';
import { useMutation } from '@tanstack/react-query';

import { ApiError } from '../api/client';
import {
  addTabItem,
  getTab,
  patchTabItem,
  removeTabItem,
  settleTab,
} from '../api/endpoints/tabs';
import { createMutationAttempt, isDefiniteMutationFailure } from '../lib/mutation-attempt';
import { isVersionConflict } from '../lib/version-conflict';
import type { ChargePaymentMethod, MenuProduct, TabRecord } from '../api/schemas';
import { formatMoneyCents } from '../lib/format';
// A comanda's settle carries no reference, so it offers only the methods that need none.
import { PAYMENT_METHODS_WITHOUT_REFERENCE as PAYMENT_METHODS } from '../lib/paymentMethods';
import {
  equalSplitShareCents,
  isUnpaid,
  settledMode,
  slotTotalCents,
  unpaidGuestSlots,
  unpaidSplitParts,
  SHARED_SLOT,
} from '../lib/tabTotals';
import { ErrorBanner } from './ErrorBanner';
import { Notice } from './Notice';

/** A mesa seats people; the server caps both guest slots and split parts here. */
const MAX_GUESTS = 20;
/** How many puestos the chip row offers before the counter has used any. */
const DEFAULT_GUEST_CHIPS = 4;

type SettleMode = 'guest' | 'split';

type AddAttempt = {
  productId: string;
  guestSlot: number | null;
  idempotencyKey: string;
  fingerprint: string;
};

/** `version` is the line's version as the counter read it; a removal (quantity 0) is a delete and names none. */
type LineInput = { itemId: string; quantity?: number; guestSlot?: number | null; version?: number };

type LineAttempt = LineInput & {
  idempotencyKey: string;
  fingerprint: string;
};

type TabEditorProps = {
  tab: TabRecord;
  tableName: string;
  products: MenuProduct[];
  productsError?: string;
  /** Writes the answered tab straight into the page cache, no refetch. */
  onTabChange: (tab: TabRecord) => void;
  onClose: () => void;
};

/**
 * La comanda of one mesa: the menu on the left, la cuenta on the right, and
 * one settlement at a time. Reuses the POS surface (`styles/pos.css`) because
 * it IS the same counter work as Cobrar, only with the lines living on the
 * server instead of in transient state.
 *
 * The server prices every settlement. What the counter quotes is sent as
 * `expectedTotalCents` and only ever gets to agree; a 409 `total_mismatch`
 * re-reads the tab and re-quotes, the same recovery triangle Cobrar runs.
 */
export function TabEditor({
  tab,
  tableName,
  products,
  productsError,
  onTabChange,
  onClose,
}: TabEditorProps) {
  const [guestSlot, setGuestSlot] = useState<number | null>(SHARED_SLOT);
  const [mode, setMode] = useState<SettleMode>('guest');
  const [parts, setParts] = useState(2);
  const [paymentMethod, setPaymentMethod] = useState<ChargePaymentMethod>('efectivo');
  const [mismatch, setMismatch] = useState<string | undefined>();
  const [lastSettled, setLastSettled] = useState<number | undefined>();

  // Mutation variables carry the key for one logical action. An ambiguous
  // transport failure queues that exact key for the next identical tap; a
  // definitive API response ends the attempt. Queues (rather than one shared
  // holder) also preserve distinct keys when two identical taps are already
  // in flight.
  const addRetryKeys = useRef(createAmbiguousRetryKeys()).current;
  const lineRetryKeys = useRef(createAmbiguousRetryKeys()).current;
  const settlementAttempt = useRef(createMutationAttempt<Parameters<typeof settleTab>[1]>()).current;

  const lockedMode = settledMode(tab);
  const activeMode = lockedMode ?? mode;
  const closed = tab.status === 'settled';

  /**
   * Re-reads the tab and says why: what the counter had on screen is stale, and guessing at the difference is
   * how a guest gets charged twice. If the read fails, the tab has to be opened again.
   */
  async function rereadTab(message: string) {
    try {
      onTabChange(await getTab(tab.id));
      setMismatch(message);
    } catch {
      setMismatch('La cuenta cambió. Vuelve a abrirla para ver el total al día.');
    }
  }

  const addMutation = useMutation({
    mutationFn: (attempt: AddAttempt) =>
      addTabItem(
        tab.id,
        { productId: attempt.productId, guestSlot: attempt.guestSlot },
        attempt.idempotencyKey,
      ),
    onSuccess: onTabChange,
    onError: (error: unknown, attempt: AddAttempt) => {
      if (!isDefiniteMutationFailure(error)) {
        addRetryKeys.retain(attempt.fingerprint, attempt.idempotencyKey);
      }
    },
  });
  const lineMutation = useMutation({
    mutationFn: (attempt: LineAttempt) =>
      attempt.quantity === 0
        ? removeTabItem(tab.id, attempt.itemId, attempt.idempotencyKey)
        : patchTabItem(
            tab.id,
            attempt.itemId,
            {
              ...(attempt.quantity === undefined ? { guestSlot: attempt.guestSlot } : { quantity: attempt.quantity }),
              ...(attempt.version !== undefined ? { expectedVersion: attempt.version } : {}),
            },
            attempt.idempotencyKey,
          ),
    onSuccess: onTabChange,
    onError: async (error: unknown, attempt: LineAttempt) => {
      if (!isDefiniteMutationFailure(error)) {
        lineRetryKeys.retain(attempt.fingerprint, attempt.idempotencyKey);
      }
      // Somebody changed this line first: the counter reads the tab again and shows the server's words.
      if (isVersionConflict(error)) await rereadTab(error.message);
    },
  });

  const settleMutation = useMutation({
    mutationFn: (input: Parameters<typeof settleTab>[1]) => {
      const original = settlementAttempt.begin(input);
      return settleTab(tab.id, original.input, original.key);
    },
    onSuccess: (settlement) => {
      settlementAttempt.complete();
      setMismatch(undefined);
      setLastSettled(settlement.amountCents);
      onTabChange(settlement.tab);
    },
    onError: async (error: unknown) => {
      settlementAttempt.fail(error);
      if (settlementAttempt.isUncertain() || !(error instanceof ApiError)) return;
      if (error.code !== 'total_mismatch' && error.status !== 409) return;
      // Re-read the tab and re-quote from the server's own rows.
      await rereadTab(error.message);
    },
  });
  const settlementLocked = settleMutation.isPending || settlementAttempt.isUncertain();

  const slots = unpaidGuestSlots(tab);
  const guestChips = buildGuestChips(tab);
  const openParts = unpaidSplitParts(tab, parts);
  const lockedParts = tab.splitPayments[0]?.parts;

  function settleGuest(slot: number | null) {
    setLastSettled(undefined);
    settleMutation.mutate({
      guestSlot: slot, paymentMethod,
      expectedTotalCents: slotTotalCents(tab, slot),
    });
  }

  function settlePart(part: number) {
    setLastSettled(undefined);
    settleMutation.mutate({
      equalSplit: { parts: lockedParts ?? parts, part }, paymentMethod,
      expectedTotalCents: equalSplitShareCents(tab.totalCents, lockedParts ?? parts, part),
    });
  }

  function addProduct(product: MenuProduct) {
    const fingerprint = `add:${product.id}:${guestSlot ?? 'shared'}`;
    addMutation.mutate({
      productId: product.id,
      guestSlot,
      fingerprint,
      idempotencyKey: addRetryKeys.take(fingerprint),
    });
  }

  function changeLine(input: LineInput) {
    const fingerprint = lineFingerprint(input);
    lineMutation.mutate({
      ...input,
      fingerprint,
      idempotencyKey: lineRetryKeys.take(fingerprint),
    });
  }

  return (
    <section className="pos" aria-label={`Cuenta de ${tableName}`}>
      <section aria-label="Menú">
        <h2 className="sr-only">Menú</h2>
        {productsError ? <ErrorBanner message={productsError} /> : null}
        {!closed ? (
          <>
          <p className="pos-pane-label">Para quién es lo próximo</p>
          <div className="chip-row" role="radiogroup" aria-label="Puesto">
            {guestChips.map((chip) => (
              <button
                key={String(chip)}
                type="button"
                role="radio"
                aria-checked={chip === guestSlot}
                disabled={settlementLocked}
                className="chip"
                data-status={chip === guestSlot ? 'active' : undefined}
                onClick={() => setGuestSlot(chip)}
              >
                {slotLabel(chip)}
              </button>
            ))}
          </div>
          </>
        ) : null}
        <div className="pos-catalog" style={{ marginTop: 'var(--space-3)' }}>
          {products.map((product) => (
            <button
              key={product.id}
              type="button"
              className="pos-tile"
              disabled={closed || addMutation.isPending || settlementLocked}
              onClick={() => addProduct(product)}
            >
              <span className="pos-tile-name">{product.name}</span>
              <span className="pos-tile-foot">
                <span className="pos-tile-price">{formatMoneyCents(product.priceCents)}</span>
              </span>
            </button>
          ))}
        </div>
        {products.length === 0 && !productsError ? (
          <p className="row-sub">
            Todavía no hay platos en el menú. La persona dueña debe agregarlos en Menú.
          </p>
        ) : null}
        {addMutation.isError ? <ErrorBanner message={errorMessage(addMutation.error)} /> : null}
      </section>

      <aside className="pos-pane" aria-label="La cuenta">
        <div className="pos-pane-head">
          <h2 className="pos-pane-label">{tableName}</h2>
          <button type="button" className="btn small ghost" disabled={settlementLocked} onClick={onClose}>
            Volver al plano
          </button>
        </div>
        {mismatch ? <ErrorBanner message={mismatch} /> : null}
        {lastSettled !== undefined ? (
          <Notice>Cobrado {formatMoneyCents(lastSettled)}.</Notice>
        ) : null}

        {tab.items.length === 0 ? (
          <p className="pos-empty">
            Todavía no agregaste nada.
            <span className="hint">Toca un plato del menú para empezar la cuenta.</span>
          </p>
        ) : (
          <ul className="cart-lines">
            {tab.items.map((item) => (
              <li key={item.id} className="cart-line">
                <div>
                  <p className="cart-line-name">{item.name}</p>
                  <p className="cart-line-unit">
                    {formatMoneyCents(item.unitPriceCents)}
                    {item.quantity > 1 ? ' c/u' : ''}
                    {isUnpaid(item) ? null : (
                      <>
                        {' · '}
                        <span className="status" data-tone="good">
                          Pagado
                        </span>
                      </>
                    )}
                  </p>
                  {isUnpaid(item) && !closed ? (
                    <select
                      className="cart-line-slot"
                      aria-label={`Puesto de ${item.name}`}
                      value={item.guestSlot === null ? '' : String(item.guestSlot)}
                      disabled={lineMutation.isPending || settlementLocked}
                      onChange={(event) =>
                        changeLine({
                          itemId: item.id,
                          guestSlot: event.target.value === '' ? SHARED_SLOT : Number(event.target.value),
                          version: item.version,
                        })
                      }
                    >
                      {guestChips.map((chip) => (
                        <option key={String(chip)} value={chip === null ? '' : String(chip)}>
                          {slotLabel(chip)}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <p className="cart-line-unit">{slotLabel(item.guestSlot)}</p>
                  )}
                </div>
                {isUnpaid(item) && !closed ? (
                  <span className="stepper">
                    <button
                      type="button"
                      aria-label={`Quitar una unidad de ${item.name}`}
                      disabled={lineMutation.isPending || settlementLocked}
                      onClick={() =>
                        changeLine({ itemId: item.id, quantity: item.quantity - 1, version: item.version })
                      }
                    >
                      −
                    </button>
                    <span className="qty">{item.quantity}</span>
                    <button
                      type="button"
                      aria-label={`Agregar una unidad de ${item.name}`}
                      disabled={lineMutation.isPending || settlementLocked}
                      onClick={() =>
                        changeLine({ itemId: item.id, quantity: item.quantity + 1, version: item.version })
                      }
                    >
                      +
                    </button>
                  </span>
                ) : (
                  <span className="cart-line-unit">× {item.quantity}</span>
                )}
                <p className="amount">{formatMoneyCents(item.lineTotalCents)}</p>
              </li>
            ))}
          </ul>
        )}
        {lineMutation.isError && !isVersionConflict(lineMutation.error) ? <ErrorBanner message={errorMessage(lineMutation.error)} /> : null}

        <div className="pos-totals">
          {tab.unsettledCents !== tab.totalCents ? (
            <p className="line">
              Total <span className="amount">{formatMoneyCents(tab.totalCents)}</span>
            </p>
          ) : null}
          <p className="line total">
            Por cobrar <span className="amount">{formatMoneyCents(tab.unsettledCents)}</span>
          </p>
        </div>

        {closed ? (
          <p className="row-sub">
            <span className="status" data-tone="good">
              Cuenta cerrada
            </span>
          </p>
        ) : (
          <>
            <div>
              <p className="pos-pane-label">Cómo se paga</p>
              <div className="seg" role="radiogroup" aria-label="Cómo se paga" style={{ marginTop: 8 }}>
                <button
                  type="button"
                  aria-pressed={activeMode === 'guest'}
                  disabled={Boolean(lockedMode) || settlementLocked}
                  onClick={() => setMode('guest')}
                >
                  Por puesto
                </button>
                <button
                  type="button"
                  aria-pressed={activeMode === 'split'}
                  disabled={Boolean(lockedMode) || settlementLocked}
                  onClick={() => setMode('split')}
                >
                  Partes iguales
                </button>
              </div>
            </div>

            <div>
              <p className="pos-pane-label">Método de pago</p>
              <div className="seg" role="radiogroup" aria-label="Método de pago" style={{ marginTop: 8 }}>
                {PAYMENT_METHODS.map((method) => (
                  <button
                    key={method.value}
                    type="button"
                    aria-pressed={paymentMethod === method.value}
                    disabled={settlementLocked}
                    onClick={() => setPaymentMethod(method.value)}
                  >
                    {method.label}
                  </button>
                ))}
              </div>
            </div>

            {settleMutation.isError && !mismatch ? (
              <ErrorBanner message={errorMessage(settleMutation.error)} />
            ) : null}
            {settlementAttempt.isUncertain() ? <button type="button" className="btn" disabled={settleMutation.isPending}
              onClick={() => { const original = settlementAttempt.pending(); if (original) settleMutation.mutate(original.input); }}>
              Reintentar cobro
            </button> : null}

            {activeMode === 'guest' ? (
              <div className="form-grid">
                {slots.length === 0 ? (
                  <p className="row-sub">No hay nada por cobrar todavía.</p>
                ) : (
                  slots.map((slot) => (
                    <button
                      key={String(slot)}
                      type="button"
                      className={slots.length === 1 ? 'btn' : 'btn secondary'}
                      disabled={settlementLocked}
                      onClick={() => settleGuest(slot)}
                    >
                      Cobrar {slotLabel(slot).toLocaleLowerCase('es-PA')}{' '}
                      {formatMoneyCents(slotTotalCents(tab, slot))}
                    </button>
                  ))
                )}
              </div>
            ) : (
              <div className="form-grid">
                <div className="seg mini" role="radiogroup" aria-label="Entre cuántas partes">
                  {[2, 3, 4, 5, 6].map((option) => (
                    <button
                      key={option}
                      type="button"
                      aria-pressed={(lockedParts ?? parts) === option}
                      disabled={lockedParts !== undefined || option > MAX_GUESTS || settlementLocked}
                      onClick={() => setParts(option)}
                    >
                      {option}
                    </button>
                  ))}
                </div>
                {openParts.map((part) => (
                  <button
                    key={part}
                    type="button"
                    className={openParts.length === 1 ? 'btn' : 'btn secondary'}
                    disabled={settlementLocked || tab.totalCents === 0}
                    onClick={() => settlePart(part)}
                  >
                    Cobrar parte {part}{' '}
                    {formatMoneyCents(
                      equalSplitShareCents(tab.totalCents, lockedParts ?? parts, part),
                    )}
                  </button>
                ))}
              </div>
            )}
          </>
        )}
      </aside>
    </section>
  );
}

/**
 * The puestos the counter can point at: whatever the tab already uses, plus
 * enough fresh ones to seat the next guest, and always the shared line.
 */
function buildGuestChips(tab: TabRecord): (number | null)[] {
  const used = tab.items
    .map((item) => item.guestSlot)
    .filter((slot): slot is number => slot !== null);
  const highest = used.length > 0 ? Math.max(...used) : 0;
  const count = Math.min(Math.max(highest + 1, DEFAULT_GUEST_CHIPS), MAX_GUESTS);
  return [SHARED_SLOT, ...Array.from({ length: count }, (_unused, index) => index + 1)];
}

function slotLabel(slot: number | null): string {
  return slot === null ? 'Compartido' : `Puesto ${slot}`;
}

/**
 * One logical edit: the same line, the same change, read at the same version.
 * The version is part of it because the Idempotency-Key kept after an ambiguous
 * failure may only come back with the same body; a body with another
 * expectedVersion is a new action.
 */
function lineFingerprint(input: LineInput): string {
  if (input.quantity === 0) return `remove:${input.itemId}`;
  const version = input.version ?? 'none';
  if (input.quantity !== undefined) return `quantity:${input.itemId}:${input.quantity}:v${version}`;
  return `guest-slot:${input.itemId}:${input.guestSlot ?? 'shared'}:v${version}`;
}

function createAmbiguousRetryKeys() {
  const byFingerprint = new Map<string, string[]>();

  return {
    take(fingerprint: string): string {
      const keys = byFingerprint.get(fingerprint);
      const key = keys?.shift();
      if (keys?.length === 0) byFingerprint.delete(fingerprint);
      return key ?? crypto.randomUUID();
    },
    retain(fingerprint: string, key: string) {
      const keys = byFingerprint.get(fingerprint) ?? [];
      if (!keys.includes(key)) keys.push(key);
      byFingerprint.set(fingerprint, keys);
    },
  };
}

function errorMessage(error: unknown): string {
  if (error instanceof ApiError) return error.message;
  return 'No pudimos completar eso ahora mismo.';
}
